import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';

import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import SearchField from 'components/SearchField';
import ScrollableTableContainer from 'components/table/ScrollableTableContainer';
import TableActionButton from 'components/table/TableActionButton';
import TableActionCell from 'components/table/TableActionCell';
import TableActions from 'components/table/TableActions';
import TableStatusChip from 'components/table/TableStatusChip';

import api, { resolveAssetUrl } from 'utils/api';
import { useToast } from 'contexts/ToastContext';
import brand from 'utils/designTokens';

const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#fff',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': {
      borderColor: brand.border
    },
    '&:hover fieldset': {
      borderColor: brand.borderHover
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${brand.overlay}`,
      '& fieldset': {
        borderColor: 'primary.main'
      }
    }
  }
};

export default function ProductSubCategoryList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, showApiError } = useToast();

  const queryParams = new URLSearchParams(location.search);
  const initialCategoryId = queryParams.get('category_id') || '';

  // Data states
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Pagination states (MUI TablePagination uses 0-indexed page)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters & Search
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState(initialCategoryId);

  // Action states
  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Load Categories for parent filter dropdown
  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get('/product/category/list');
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      setCategories(items);
    } catch (err) {
      console.error('Failed to load categories for filter:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch Subcategories from Backend API
  const fetchSubCategories = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };

      if (filterCategoryId && filterCategoryId !== 'all') {
        params.category_id = filterCategoryId;
      }

      if (searchDebounced) {
        params.search = searchDebounced;
      }

      const res = await api.get('/product/sub/category/list', { params });
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(items);

      const total =
        typeof res.data?.pagination?.total === 'number'
          ? res.data.pagination.total
          : items.length;
      setTotalCount(total);
    } catch (err) {
      showApiError(err, 'Failed to fetch sub-categories');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterCategoryId, searchDebounced, showApiError]);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  // Handle Search Input Change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  // Handle Category Filter Change
  const handleCategoryFilterChange = (e) => {
    setFilterCategoryId(e.target.value);
    setPage(0);
  };

  // Refresh handler
  const handleRefresh = () => {
    fetchSubCategories();
  };

  // Handle Status Toggle (PUT /product/sub/category/manage/status/:id)
  const handleToggleStatus = async (sub) => {
    if (togglingId) return; // Prevent concurrent toggles
    const targetId = sub.id;
    const nextStatus = !sub.status;
    setTogglingId(targetId);

    try {
      const res = await api.put(`/product/sub/category/manage/status/${targetId}`, {
        status: nextStatus
      });
      const updatedStatus = res.data?.data?.status ?? nextStatus;
      setRows((prev) =>
        prev.map((r) => (r.id === targetId ? { ...r, status: updatedStatus } : r))
      );
      showToast(
        res.data?.message || `Sub-category status set to ${updatedStatus ? 'Active' : 'Inactive'}`,
        'success'
      );
    } catch (err) {
      showApiError(err, 'Failed to update sub-category status');
    } finally {
      setTogglingId(null);
    }
  };

  // Handle Delete (DELETE /product/sub/category/delete/:id)
  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);

    try {
      const res = await api.delete(`/product/sub/category/delete/${deleteTarget.id}`);
      showToast(res.data?.message || 'Sub-category deleted successfully', 'success');
      setDeleteTarget(null);

      // If this was the last item on page > 0, go back one page
      if (rows.length === 1 && page > 0) {
        setPage((p) => p - 1);
      } else {
        fetchSubCategories();
      }
    } catch (err) {
      showApiError(err, 'Failed to delete sub-category');
    } finally {
      setDeleting(false);
    }
  };

  const getImageUrl = (sub) => {
    const rawUrl =
      sub.imageUrl ||
      sub.image_url ||
      sub.imageDoc?.imageUrl ||
      sub.imageDoc?.attachment_url;
    return rawUrl ? resolveAssetUrl(rawUrl) : null;
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        variant="nested"
        onBack={() => navigate(-1)}
        backTitle="Back"
        title="Product Sub Categories"
        subtitle="Manage sub-categories linked to product categories."
        count={totalCount}
        actionLabel="Add Sub Category"
        actionIcon={AddIcon}
        onAction={() =>
          navigate(
            filterCategoryId
              ? `/product/sub-category/create?category_id=${encodeURIComponent(filterCategoryId)}`
              : '/product/sub-category/create'
          )
        }
      />

      <MainCard contentSX={{ p: 0 }}>
        {/* Filters and Search Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ flex: 1, minWidth: 280 }}>
            {/* Search Input */}
            <SearchField
              placeholder="Search by English or Arabic name…"
              value={search}
              onChange={handleSearchChange}
              sx={{ minWidth: { xs: '100%', sm: 260 }, mb: 0 }}
            />

            {/* Parent Category Dropdown Filter */}
            <FormControl size="small" sx={{ minWidth: 200, ...filterFieldSx }}>
              <InputLabel id="subcategory-parent-category-filter-label">Parent Category</InputLabel>
              <Select
                labelId="subcategory-parent-category-filter-label"
                value={filterCategoryId}
                label="Parent Category"
                onChange={handleCategoryFilterChange}
                disabled={loadingCategories}
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name_en}
                    {cat.name_ar ? ` (${cat.name_ar})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Refresh Action */}
          <Tooltip title="Refresh" arrow>
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 1
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Table Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={36} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 3, color: 'text.secondary' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {searchDebounced || filterCategoryId
                ? 'No sub-categories match your filters'
                : 'No sub-categories found'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {searchDebounced || filterCategoryId
                ? 'Try adjusting your search keywords or clearing the category filter.'
                : 'Get started by creating your first product sub-category.'}
            </Typography>
            {!searchDebounced && !filterCategoryId && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/product/sub-category/create')}
                sx={{ borderRadius: 2 }}
              >
                Create Sub Category
              </Button>
            )}
          </Box>
        ) : (
          <>
            <ScrollableTableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    {['#', 'Image', 'Name (EN)', 'Name (AR)', 'Category', 'Sequence', 'Status', 'Actions'].map(
                      (col, i) => (
                        <TableCell
                          key={col}
                          align={col === 'Actions' ? 'center' : 'left'}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            bgcolor: 'grey.50',
                            whiteSpace: 'nowrap',
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            py: 1.5,
                            ...(col === '#' && { width: 48, color: 'text.disabled' })
                          }}
                        >
                          {col}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((sub, index) => {
                    const imageUrl = getImageUrl(sub);
                    const categoryName = sub.category?.name_en || '—';

                    return (
                      <TableRow key={sub.id} hover sx={{ verticalAlign: 'middle' }}>
                        {/* # */}
                        <TableCell sx={{ color: 'text.disabled', fontSize: 13, width: 48 }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        {/* Image */}
                        <TableCell sx={{ width: 64 }}>
                          <Avatar
                            src={imageUrl || undefined}
                            variant="rounded"
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: 'primary.lighter',
                              color: 'primary.main',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            {!imageUrl && (sub.name_en?.[0]?.toUpperCase() || '?')}
                          </Avatar>
                        </TableCell>

                        {/* Name EN */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {sub.name_en || '—'}
                          </Typography>
                        </TableCell>

                        {/* Name AR */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ direction: 'rtl', color: 'text.secondary' }}
                          >
                            {sub.name_ar || '—'}
                          </Typography>
                        </TableCell>

                        {/* Parent Category */}
                        <TableCell>
                          <Chip
                            label={categoryName}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500, borderColor: 'divider' }}
                          />
                        </TableCell>

                        {/* Sequence */}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {sub.sequence ?? 1}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <TableStatusChip
                            active={Boolean(sub.status)}
                            loading={togglingId === sub.id}
                            onClick={() => handleToggleStatus(sub)}
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableActionCell>
                          <TableActions>
                            <TableActionButton
                              action="edit"
                              title="Edit Sub Category"
                              onClick={() => navigate(`/product/sub-category/edit/${sub.id}`)}
                            />
                            <TableActionButton
                              action="delete"
                              title="Delete Sub Category"
                              onClick={() => setDeleteTarget({ id: sub.id, name: sub.name_en })}
                            />
                          </TableActions>
                        </TableActionCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollableTableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
            />
          </>
        )}
      </MainCard>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Sub Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            size="small"
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: '#b71c1c',
              color: '#fff',
              '&:hover': { bgcolor: '#8e0000' },
              '&.Mui-disabled': { bgcolor: 'rgba(183, 28, 28, 0.45)', color: '#fff' }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
